import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useMediaQuery } from './viewport';

/**
 * Détection d'installabilité PWA (spec 12 §3). Deux moyens selon la plateforme :
 * - **Chromium** (Chrome/Edge desktop & Android) : l'événement `beforeinstallprompt`
 *   est capté GLOBALEMENT dès le chargement de ce module (il part très tôt, avant le
 *   premier render) puis rejoué à la demande via `promptInstall()`.
 * - **iOS/iPadOS** : aucune API — on se contente de détecter la plateforme pour
 *   afficher le tutoriel « Sur l'écran d'accueil » (composant en 12c).
 *
 * Le composant/popin et le toast « installé 🐾 » (sur `appinstalled`) sont câblés
 * par le consommateur en 12c ; ici on n'expose que la détection (hook + helpers purs).
 */

/** L'événement Chromium (hors lib DOM standard). */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

export type PwaPlatform = 'chromium' | 'ios' | 'other';

export interface UsePwaInstall {
  /** Déjà en mode application (standalone) → ne rien proposer. */
  isInstalled: boolean;
  platform: PwaPlatform;
  /** Un `beforeinstallprompt` a été capté → le prompt natif est disponible. */
  canPrompt: boolean;
  /** Déclenche le prompt natif (usage unique) ; `dismissed` si rien à proposer. */
  promptInstall(): Promise<'accepted' | 'dismissed'>;
}

// --- État global capté (singleton module) ------------------------------------

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installedThisSession = false;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

function onBeforeInstallPrompt(event: Event): void {
  // Empêche le mini-infobar Chromium par défaut : on pilote nous-mêmes le moment (§5).
  event.preventDefault();
  deferredPrompt = event as BeforeInstallPromptEvent;
  emit();
}

function onAppInstalled(): void {
  installedThisSession = true;
  deferredPrompt = null; // plus rien à proposer une fois installé
  emit();
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onAppInstalled);
}

/** S'abonne aux changements d'état capté (pour `useSyncExternalStore`). */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Un événement `beforeinstallprompt` est actuellement disponible à rejouer. */
export function canPromptInstall(): boolean {
  return deferredPrompt !== null;
}

/** L'app a émis `appinstalled` durant cette session. */
export function wasInstalledThisSession(): boolean {
  return installedThisSession;
}

/**
 * Rejoue le prompt natif d'installation (Chromium). Usage unique : l'événement est
 * consommé (re-capté au prochain `beforeinstallprompt`). Renvoie `dismissed` si aucun
 * événement n'est disponible (l'appelant ne devrait afficher le bouton que si `canPrompt`).
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed'> {
  const event = deferredPrompt;
  if (!event) return 'dismissed';
  await event.prompt();
  const { outcome } = await event.userChoice;
  deferredPrompt = null;
  emit();
  return outcome;
}

// --- Détection pure (injectable, pour la matrice de tests) --------------------

type NavigatorLike = Pick<Navigator, 'userAgent'> & {
  maxTouchPoints?: number;
  /** iOS Safari (legacy) : app installée sur l'écran d'accueil. */
  standalone?: boolean;
};

/**
 * `'ios'` pour iPhone/iPad (y compris iPadOS en UA desktop : `Macintosh` +
 * `maxTouchPoints > 1`), `'chromium'` si `beforeinstallprompt` est supporté, sinon
 * `'other'`. Sur iOS, TOUS les navigateurs offrent « Sur l'écran d'accueil » (16.4+).
 */
export function detectPlatform(
  nav: NavigatorLike = navigator,
  win: object = typeof window !== 'undefined' ? window : {},
): PwaPlatform {
  const ua = nav.userAgent ?? '';
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && (nav.maxTouchPoints ?? 0) > 1);
  if (isIOS) return 'ios';
  if ('onbeforeinstallprompt' in win) return 'chromium';
  return 'other';
}

/** `true` si l'app tourne en mode application (installée). `mediaStandalone` = résultat
 * de `matchMedia('(display-mode: standalone)')` (fourni par le hook, réactif). */
export function isStandalone(mediaStandalone: boolean, nav: NavigatorLike = navigator): boolean {
  return mediaStandalone || nav.standalone === true;
}

// --- Hook --------------------------------------------------------------------

/**
 * Expose l'état d'installabilité PWA (spec 12 §3). `isInstalled` combine le mode
 * d'affichage (`display-mode: standalone`, réactif), le drapeau iOS legacy
 * (`navigator.standalone`) et l'événement `appinstalled` de la session en cours.
 */
export function usePwaInstall(): UsePwaInstall {
  const mediaStandalone = useMediaQuery('(display-mode: standalone)');
  const canPrompt = useSyncExternalStore(subscribe, canPromptInstall, () => false);
  const installed = useSyncExternalStore(subscribe, wasInstalledThisSession, () => false);

  const platform = useMemo(() => detectPlatform(), []);
  const isInstalled = installed || isStandalone(mediaStandalone);

  const prompt = useCallback(() => promptInstall(), []);

  return { isInstalled, platform, canPrompt, promptInstall: prompt };
}

/** Réinitialise l'état capté — TESTS UNIQUEMENT (le singleton persiste sinon). */
export function __resetPwaInstallForTests(): void {
  deferredPrompt = null;
  installedThisSession = false;
}
